import { ColorAdvice4 } from 'autogen/design-tokens/tokens';
import {
    SurveyResponseCreate,
    type SurveyResponseCreateParams,
    SurveyResponseCurrentPage,
    type SurveyResponseCurrentPageParams,
    type SurveyResponseViewModel
} from 'autogen/swagger/all';
import { Next } from 'autogen/translation-keys/trans-website-paging';
import { Back } from 'autogen/translation-keys/trans-website-shared';
import { Finish } from 'autogen/translation-keys/trans-website-surveysystem';
import useMediatorState, { mediatorAtom, useMediatorValue } from 'hooks/globals/useMediatorAtom';
import i18next from 'i18next';

type SurveyQuickConfigProps = {
    color: string;
    backTranslation: string;
    nextTranslation: string;
    finishTranslation: string;
};

type SurveyQuickViewAtomProps = {
    isOpen: boolean;
    surveyModel: SurveyResponseViewModel;
    config: SurveyQuickConfigProps;
};

const defaultConfig = {
    color: ColorAdvice4,
    backTranslation: i18next.t(Back),
    nextTranslation: i18next.t(Next),
    finishTranslation: i18next.t(Finish)
};

export const SurveyQuickViewAtom = mediatorAtom<SurveyQuickViewAtomProps>({
    isOpen: false,
    surveyModel: {} as SurveyResponseViewModel,
    config: defaultConfig
});

/**
 * Important notes about Survey.
 *
 * A surveyId describes a specific survey.
 * A ResponseId describes an ongoing session in a survey.
 *
 * The Survey component always expects a SurveyResponseViewModel as it's initial model to start. that model can both be from an ongoing survey, or a completely new instance.
 * That's why we have 3 ways of opening a survey quickView:
 * 1. Open by SurveyId (a completely new survey is started and the model is fetched and passed to the Survey component)
 * 2. Open by ResponseId (an ongoing survey model is fetched and passed to the Survey component, resuming the progress in the survey, due to the dynamic nature of the surveys a currentPage number also has to be passed)
 * 3. Open by SurveyResponseViewModel (No fetching is done here, you explicitly pass a model and we resume the survey progress based on the model you've passed)
 * @returns
 */
export function useOpenSurveyQuickView() {
    const [{ isOpen, surveyModel }, setMediatorState] = useMediatorState(SurveyQuickViewAtom);

    /**
     * A setter to update the state and merge configOptions that might have changed.
     * @param value
     */
    const set = (value: Omit<SurveyQuickViewAtomProps, 'config'> & { config?: Partial<SurveyQuickConfigProps> }) => {
        const mergedConfigOptions: SurveyQuickConfigProps = { ...defaultConfig, ...value.config };
        setMediatorState({ ...value, config: mergedConfigOptions });
    };

    /**
     * Close survey QuickView
     */
    const closeSurveyQuickView = () => {
        setMediatorState((previousState) => {
            return { ...previousState, isOpen: false, config: defaultConfig };
        });
    };

    /**
     * Open survey QuickView using a surveyId ( will create a new instance of a survey)
     * @param surveyParams (surveyId, returnUrl)
     * @param config configuration options for the surveyQuickView
     */
    const openSurveyQuickViewBySurveyId = async (
        surveyParams: SurveyResponseCreateParams,
        config?: Partial<SurveyQuickConfigProps>
    ) => {
        try {
            const surveyModel = await SurveyResponseCreate({
                surveyId: surveyParams.surveyId,
                returnUrl: surveyParams.returnUrl
            });
            set({
                isOpen: true,
                surveyModel,
                config
            });
        } catch (error) {
            console.error(error);
        }
    };

    /**
     * Open survey QuickView using a responseId ( will continue an existing survey)
     * @param surveyParams (surveyResponseId, currentPage)
     * @param config configuration options for the surveyQuickView
     */
    const openSurveyQuickViewByResponseId = async (
        surveyParams: SurveyResponseCurrentPageParams,
        config?: Partial<SurveyQuickConfigProps>
    ) => {
        try {
            const surveyModel = await SurveyResponseCurrentPage({
                surveyResponseId: surveyParams.surveyResponseId,
                currentPage: surveyParams.currentPage
            });
            set({
                isOpen: true,
                surveyModel,
                config
            });
        } catch (error) {
            console.error(error);
        }
    };

    /**
     * Open survey QuickView using a SurveyResponseViewModel ( will continue a survey from the model passed)
     * @param surveyModel SurveyResponseViewModel
     * @param config configuration options for the surveyQuickView
     */
    const openSurveyQuickViewBySurveyModel = (
        surveyModel: SurveyResponseViewModel,
        config?: Partial<SurveyQuickConfigProps>
    ) => {
        set({
            isOpen: true,
            surveyModel,
            config
        });
    };

    return {
        openSurveyQuickViewBySurveyId,
        openSurveyQuickViewByResponseId,
        openSurveyQuickViewBySurveyModel,
        closeSurveyQuickView,
        isOpen,
        surveyModel
    };
}

export function useSurveyQuickViewConfigProperties() {
    const { config } = useMediatorValue(SurveyQuickViewAtom);

    return config;
}
